// import PropTypes from 'prop-types';
// import { useCallback, useEffect, useMemo, useState } from 'react';
// import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
// import { Badge, Collapse, ListGroup } from 'react-bootstrap';
// import NavItem from './NavItem';
// import { useGetMenuMaster } from 'api/menu';
// import { useSelector } from 'react-redux';

// export default function NavCollapse({
//   menu,
//   level,
//   setSelectedItems,
//   selectedItems,
//   setSelectedLevel,
//   selectedLevel
// }) {
//   const { menuMaster } = useGetMenuMaster();
//   const navigate = useNavigate();
//   const drawerOpen = menuMaster?.isDashboardDrawerOpened;
//   const { pathname } = useLocation();
//   const permissions = useSelector((state) => state.auth.permissions);

//   const [open, setOpen] = useState(false);

//   // ✅ Central active matcher (supports dynamic routes)
//   const isMenuActive = useCallback((menuItem, currentPath) => {
//     if (menuItem.matchPath) {
//       return matchPath({ path: menuItem.matchPath, end: false }, currentPath);
//     }

//     if (menuItem.url) {
//       return matchPath({ path: menuItem.url, end: false }, currentPath);
//     }

//     if (menuItem.type === 'collapse' && Array.isArray(menuItem.children)) {
//       return menuItem.children.some((child) =>
//         isMenuActive(child, currentPath)
//       );
//     }

//     return false;
//   }, []);

//   // ✅ Keep parent open if any child is active (reload-safe)
//   useEffect(() => {
//     if (!menu.children) return;

//     const hasActiveChild = menu.children.some((child) =>
//       isMenuActive(child, pathname)
//     );

//     if (hasActiveChild) {
//       setOpen(true);
//       setSelectedLevel(level);
//       setSelectedItems(menu);
//     } else if (selectedLevel !== level) {
//       setOpen(false);
//     }
//   }, [pathname, menu.children, level, selectedLevel, isMenuActive, setSelectedItems, setSelectedLevel, menu]);

//   const handleClick = () => {
//     const nextOpen = !open;
//     setOpen(nextOpen);
//     setSelectedLevel(level);
//     setSelectedItems(nextOpen ? menu : undefined);

//     if (menu.url) {
//       navigate(menu.url);
//     }
//   };

//   const hasPermission = (permission) => {
//   if (!permission) return true;
//   return permissions?.includes(permission);
// };

//   // ✅ Render children cleanly
//   const renderedChildren = useMemo(() => {
//     return menu.children?.map((item) => {
//       if (item.hideInMenu) return null;
//       if (menu.permission && !hasPermission(menu.permission)) {
//             return null;
//           }

//       switch (item.type) {
//         case 'collapse':
//           return (
//             <NavCollapse
//               key={item.id}
//               menu={item}
//               level={level + 1}
//               {...{
//                 setSelectedItems,
//                 selectedItems,
//                 setSelectedLevel,
//                 selectedLevel
//               }}
//             />
//           );

//         case 'item':
//           return <NavItem key={item.id} item={item} level={level + 1} />;

//         default:
//           return null;
//       }
//     });
//   }, [
//     menu.children,
//     level,
//     setSelectedItems,
//     selectedItems,
//     setSelectedLevel,
//     selectedLevel
//   ]);

//   return (
//     <ListGroup className={`pc-item pc-hasmenu ${open ? 'pc-trigger' : ''}`}>
//       <Link className="pc-link" to="#!" onClick={handleClick}>
//         {menu.icon && (
//           <span className="pc-micon">
//             <i className={menu.icon} />
//           </span>
//         )}
//         <span className="pc-mtext">{menu.title}</span>
//         <span className="pc-arrow">
//           <i className="ti ti-chevron-right" />
//         </span>
//         {menu.badge && <Badge className="pc-badge">{menu.badge}</Badge>}
//       </Link>

//       <Collapse in={open}>
//         <div>
//           <ul className="pc-submenu">{renderedChildren}</ul>
//         </div>
//       </Collapse>
//     </ListGroup>
//   );
// }

// NavCollapse.propTypes = {
//   menu: PropTypes.object.isRequired,
//   level: PropTypes.number.isRequired,
//   setSelectedItems: PropTypes.func,
//   selectedItems: PropTypes.any,
//   setSelectedLevel: PropTypes.func,
//   selectedLevel: PropTypes.number
// };

import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Collapse, ListGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

// project imports
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';
import { useTranslation } from 'react-i18next';

export default function NavCollapse({
  menu,
  level,
  setSelectedItems,
  selectedItems,
  setSelectedLevel,
  selectedLevel
}) {
  const { menuMaster } = useGetMenuMaster();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation()
  const permissions = useSelector((state) => state.auth.permissions);

  const [open, setOpen] = useState(false);

  // ==============================
  // Permission helper
  // ==============================
  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true;
      if (!Array.isArray(permissions)) return false;
      return permissions.includes(permission);
    },
    [permissions]
  );
  
  if (menu.permission && !hasPermission(menu.permission)) {
    return null;
  }

  // ==============================
  // Active route matcher (reload safe)
  // ==============================
  const isMenuActive = useCallback(
    (menuItem, currentPath) => {
      if (menuItem.matchPath) {
        return matchPath({ path: menuItem.matchPath, end: false }, currentPath);
      }

      if (menuItem.url) {
        return matchPath({ path: menuItem.url, end: false }, currentPath);
      }

      if (menuItem.type === 'collapse' && Array.isArray(menuItem.children)) {
        return menuItem.children.some((child) =>
          isMenuActive(child, currentPath)
        );
      }

      return false;
    },
    []
  );

  // ==============================
  // Keep parent open if child active
  // ==============================
  useEffect(() => {
    if (!menu.children) return;

    const hasActiveChild = menu.children.some((child) =>
      isMenuActive(child, pathname)
    );

    if (hasActiveChild) {
      setOpen(true);
      setSelectedLevel(level);
      setSelectedItems(menu);
    } else if (selectedLevel !== level) {
      setOpen(false);
    }
  }, [
    pathname,
    menu.children,
    level,
    selectedLevel,
    isMenuActive,
    setSelectedItems,
    setSelectedLevel,
    menu
  ]);

  // ==============================
  // Click handler
  // ==============================
  const handleClick = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    setSelectedLevel(level);
    setSelectedItems(nextOpen ? menu : undefined);

    if (menu.url) {
      navigate(menu.url);
    }
  };

  // ==============================
  // Render children with permission filtering
  // ==============================
  const renderedChildren = useMemo(() => {
    return menu.children
      ?.filter((item) => {
        if (item.hideInMenu) return false;
        if (item.permission && !hasPermission(item.permission)) return false;
        return true;
      })
      .map((item) => {
        switch (item.type) {
          case 'collapse':
            return (
              <NavCollapse
                key={item.id}
                menu={item}
                level={level + 1}
                setSelectedItems={setSelectedItems}
                selectedItems={selectedItems}
                setSelectedLevel={setSelectedLevel}
                selectedLevel={selectedLevel}
              />
            );

          case 'item':
            return <NavItem key={item.id} item={item} level={level + 1} />;

          default:
            return null;
        }
      });
  }, [
    menu.children,
    level,
    hasPermission,
    setSelectedItems,
    selectedItems,
    setSelectedLevel,
    selectedLevel
  ]);

  // If no visible children → hide collapse
  if (!renderedChildren || renderedChildren.length === 0) {
    return null;
  }

  return (
    <ListGroup className={`pc-item pc-hasmenu ${open ? 'pc-trigger' : ''}`}>
      <Link className="pc-link" to="#!" onClick={handleClick}>
        {menu.icon && (
          <span className="pc-micon">
            <i className={menu.icon} />
          </span>
        )}
        <span className="pc-mtext">{t(menu.title)}</span>
        <span className="pc-arrow">
          <i className="ti ti-chevron-right" />
        </span>
        {menu.badge && <Badge className="pc-badge">{menu.badge}</Badge>}
      </Link>

      <Collapse in={open}>
        <div>
          <ul className="pc-submenu">{renderedChildren}</ul>
        </div>
      </Collapse>
    </ListGroup>
  );
}

NavCollapse.propTypes = {
  menu: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
  setSelectedItems: PropTypes.func,
  selectedItems: PropTypes.any,
  setSelectedLevel: PropTypes.func,
  selectedLevel: PropTypes.number
};

